import {inject, Injectable} from '@angular/core';
import {GridGetService, GridServiceParams, GridServiceResponse} from "../../shared/model/page-grid";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {catchError, map, Observable} from "rxjs";
import {removeEmptyParams} from "../../../infrastructure/helper/remove-empty-params";
import {SearchPaginatedAdxBookingResponse} from "../model/search-paginated-adx-booking-response";
import {PayService} from "../../shared/service/pay/pay.service";
import {Sort} from "@angular/material/sort";

@Injectable({
    providedIn: 'root'
})
export class AdxBookingGridService implements GridGetService<SearchPaginatedAdxBookingResponse> {
    private readonly baseUrl = 'dbov2/adx-booking';
    private readonly http = inject(HttpClient);
    private readonly payService = inject(PayService);

    private submitToMap: Record<string, string> = {};

    constructor() {
        this.getSubmitTo();
    }

    private getSubmitTo() {
        this.payService.getSubmitTo({all: true}).subscribe({
            next: (response) => {
                if (response && response.items) {
                    this.submitToMap = response.items.reduce((acc, item) => {
                        acc[item.submitToLinkCode] = item.submitToLink;
                        return acc;
                    }, {} as Record<string, string>);
                }
            },
            error: (error) => {
                console.error('Error fetching submit-to data:', error);
            }
        });
    }

    private mapApiResponseToTableRows(apiItems: any[]): any[] {
        const rows: any[] = [];
        if (!Array.isArray(apiItems)) return rows;
        for (const item of apiItems) {
            const adxInvoiceBookings = item.adxInvoice?.adxBookings;
            let commissionMap: Record<number, number> = {};
            if (Array.isArray(adxInvoiceBookings)) {
                for (const b of adxInvoiceBookings) {
                    if (b && typeof b.commAmt === 'number') {
                        commissionMap[b.id] = b.commAmt;
                    }
                }
            }
            if (item.adxBookings && Array.isArray(item.adxBookings)) {
                for (const booking of item.adxBookings) {
                    const submitToCode = booking.submitToCode ?? item.submitToCode ?? '';
                    rows.push({
                        ...item,
                        ...booking,
                        adxBookingParent: item,
                        adxBooking: booking,
                        fmsInvoiceNumber: booking.id,
                        submitTo: submitToCode,
                        submitToName: this.submitToMap[submitToCode] || booking.submitToName || item.submitToName || submitToCode,
                        mainPassenger: item.client?.profileName || '',
                        commission: booking.commAmt ?? 0,
                        vendor: {
                            ...item.vendor,
                            vendorName: item.vendor?.vendorName || item.adxPaymentDetails?.[0]?.paymentAccount ||
                                (item.adxBookingUdids?.find((u: any) => u.udidNo === 904) ?
                                {vendorName: item.adxBookingUdids.find((u: any) => u.udidNo === 904).udidValue} : undefined),
                        },
                        clientStatus: booking.clientPayStatus?.payStatusName || '',
                        vendorStatus: booking.vendorPayStatus?.payStatusName || '',
                        firstAgent: booking.adxAgentBookings?.[0]?.agent?.agentName || '',
                    });
                }
            } else {
                const submitToCode = item.submitToCode ?? '';
                rows.push({
                    ...item,
                    fmsInvoiceNumber: item.id,
                    submitTo: submitToCode,
                    submitToName: this.submitToMap[submitToCode] || item.submitToName || submitToCode,
                    mainPassenger: item.client?.profileName || '',
                    commission: item.commAmt ?? 0,
                    vendor: {
                        ...item.vendor,
                        vendorName: item.vendor?.vendorName || item.adxPaymentDetails?.[0]?.paymentAccount ||
                            (item.adxBookingUdids?.find((u: any) => u.udidNo === 904) ?
                            {vendorName: item.adxBookingUdids.find((u: any) => u.udidNo === 904).udidValue} : undefined),
                    },
                    clientStatus: item.clientPayStatus?.payStatusName || '',
                    vendorStatus: item.vendorPayStatus?.payStatusName || '',
                    firstAgent: item.adxAgentBookings?.[0]?.agent?.agentName || '',
                });
            }
        }
        return rows;
    }

  private mapSortPropertyName(sort?: Partial<Sort>) {
      if (!sort?.active) return '';
      const propertyNameMap: { [key: string]: string } = {
        'travelType': 'travelType.travelTypeName',
        'submitTo': 'submitToCode',
        'submitToName': 'submitToCode',
        'commission': 'commAmt',
        'vendor': 'vendor.vendorName',
        'clientStatus': 'clientPayStatus.payStatusName',
        'vendorStatus': 'vendorPayStatus.payStatusName',
        'firstAgent': 'id'
      };
      return propertyNameMap[sort.active] || sort.active;
  }

    getItems({filters, page, sort} : GridServiceParams): Observable<GridServiceResponse<SearchPaginatedAdxBookingResponse>> {
        const params: Record<string, string | number | boolean> = {
            pageSize: page?.pageSize || 25,
            pageNumber: (page?.pageIndex || 0) + 1,
            propertyName: this.mapSortPropertyName(sort),
            orderKey: sort?.direction || '',

            adxInvoiceId: filters?.['adxInvoiceId'] ?? '',
        };

        removeEmptyParams(params);

        return this.http.get<GridServiceResponse<SearchPaginatedAdxBookingResponse>>(`${this.baseUrl}`, {params: {...params}})
            .pipe(
                map(response => {
                    const items = this.mapApiResponseToTableRows(response.items);
                    return {...response, items};
                }),
                catchError((error: HttpErrorResponse) => {
                    throw new Error(error?.message || 'Server error');
                })
            );
    }
}
